import {
  ColDef,
  GridOptions,
  HeaderClassParams,
  ICellRendererParams,
} from '@ag-grid-community/core';
import { Signal } from '@angular/core';

import { AgRawTooltipParams } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { ExtendableDropdownOption } from '@shared/components/extendable-options-dropdown/extendable-options-dropdown.component';
import { ZERO_HYPHEN } from '@shared/constants/format';
import { agWidthOfAColumn } from '@shared/utils/ag-width-of-a-column';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';
import { Utils } from '@shared/utils/utils';

import { DEFAULT_MANAGE_BALANCE_SHEETS_ACCOUNTS_GRID_CONFIG } from './default-grid-config';
import {
  BalanceSheetAccount,
  BalanceSheetEntitySettingsKeys,
  BalanceSheetEntitySettingsLabels,
  GridConfigOptions,
} from './grid-config-context';
import {
  AgAttributeDropdownComponent,
  AgAttributeDropdownCustomParams,
} from '../../ag-attribute-dropdown/ag-attribute-dropdown.component';
import {
  AgInlineHeaderComponent,
  AgInlineHeaderCustomParams,
} from '../../ag-inline-header/ag-inline-header.component';
import { AgPoDropdownComponent } from '../../ag-po-dropdown/ag-po-dropdown.component';
import {
  AgProgramProjectDropdownComponent,
  AgProgramProjectDropdownCustomParams,
} from '../../ag-program-project-dropdown/ag-program-project-dropdown.component';

export function manageBalanceSheetsAccountsGridConfig(
  options: GridConfigOptions
): GridOptions<BalanceSheetAccount> {
  return {
    ...DEFAULT_MANAGE_BALANCE_SHEETS_ACCOUNTS_GRID_CONFIG,
    columnDefs: [
      {
        field: 'type',
        headerClass: 'whitespace-normal break-words',
        headerName: 'Balance Sheet Account',
        maxWidth: 320,
        minWidth: 160,
        tooltipField: 'type',
        valueFormatter: (params) => {
          const value = params.value as keyof typeof BalanceSheetEntitySettingsKeys;

          return BalanceSheetEntitySettingsLabels[value];
        },
      },
      ...getAttributeColumns(options),
      ...getProgramProjectColumns(options),
    ],
    fillOperation: (params) => {
      if (params.column.getColId() === 'PO #') {
        const allowed = options.poAttributeContext?.poOptions()?.map((po) => po.value) || [];
        const newValue = params.values[params.values.length - 1];

        if (newValue == null || newValue === '') {
          return '';
        }

        if (allowed.includes(newValue)) {
          return newValue;
        }

        return params.currentCellValue;
      }

      return params.values[params.values.length - 1];
    },
    getRowId: (params) => params.data.type,
    processCellFromClipboard: (params) => {
      // Only validate for PO # column
      if (params.column.getColId() === 'PO #') {
        const allowed = options.poAttributeContext?.poOptions()?.map((po) => po.value) || [];

        // If value is empty, allow clearing
        if (params.value == null || params.value === '') {
          return '';
        }

        // Only allow if value is in allowed list
        if (allowed.includes(params.value)) {
          return params.value;
        }

        // Block invalid paste: return current cell value
        return params.node?.data?.attributes?.[getEncodedAttributeName('PO #')];
      }

      return params.value;
    },
  };
}

const getAttributeColumns = (options: GridConfigOptions) => {
  return [...options.pivotSettings.attributes]
    .sort((a, b) => Utils.localeAlphaNumSort(a.attribute_name || '', b.attribute_name || ''))
    .map((attribute) => {
      const { primary, secondary } = options.pivotSettings;
      const isAttributeUsedInJEConfig =
        attribute.attribute_name === primary.attribute_name ||
        attribute.attribute_name === secondary.attribute_name;

      const isPO = attribute.attribute_name === 'PO #';

      let removeColumnTooltip = '';

      if (isAttributeUsedInJEConfig) {
        removeColumnTooltip = 'Cannot delete attribute(s) used in JE Configuration.';
      } else if (isPO) {
        removeColumnTooltip = 'PO # column is required in every vendor budget.';
      }

      const { getAttributeOptions, onAddAttributeOption } = options.attributesContext;

      return getAttributeColumn({
        colId: attribute.attribute_name,
        field: `attributes.${getEncodedAttributeName(attribute.attribute_name)}`,
        headerName: attribute.attribute_name,
        headerState: options.headerState,
        isHeaderDeletable: !isAttributeUsedInJEConfig && !isPO,
        isHeaderEditable: !isPO,
        isPO,
        items: () => getAttributeOptions(attribute.attribute_name),
        onAddOption: (option) => onAddAttributeOption(attribute.attribute_name, option),
        onDelete: () => options.onAttributeDelete(attribute.attribute_name),
        onRename: (newName: string) => options.onAttributeRename(attribute.attribute_name, newName),
        poOptions: isPO ? options.poAttributeContext?.poOptions : undefined,
        removeColumnTooltip,
      });
    });
};

const getProgramProjectColumns = (options: GridConfigOptions): ColDef[] => {
  return [
    {
      cellClass: 'remove-padding-in-edit-mode !p-0 whitespace-normal break-words',
      cellEditor: AgProgramProjectDropdownComponent,
      cellEditorParams: () =>
        <AgProgramProjectDropdownCustomParams>{
          items: () => options.programProjectContext.programOptions,
          onAddOption: options.programProjectContext.onAddProgramOption,
        },
      cellRenderer: attributeRenderer,
      editable: true,
      field: 'program',
      headerName: 'Program *',
      maxWidth: 320,
      minWidth: 160,
      tooltipComponentParams: <AgRawTooltipParams>{
        getNode: ({ targetNode }) => targetNode?.querySelector('div[data-value-wrapper="true"]'),
      },
      tooltipField: 'program',
    },
    {
      cellClass: 'remove-padding-in-edit-mode !p-0 whitespace-normal break-words',
      cellEditor: AgProgramProjectDropdownComponent,
      cellEditorParams: () =>
        <AgProgramProjectDropdownCustomParams>{
          items: () => options.programProjectContext.projectOptions,
          onAddOption: options.programProjectContext.onAddProjectOption,
        },
      cellRenderer: attributeRenderer,
      editable: true,
      field: 'project',
      headerName: 'Project *',
      maxWidth: 320,
      minWidth: 160,
      tooltipComponentParams: <AgRawTooltipParams>{
        getNode: ({ targetNode }) => targetNode?.querySelector('div[data-value-wrapper="true"]'),
      },
      tooltipField: 'project',
    },
  ];
};

interface GetAttributeColumnParams {
  colId: string;
  field: string;
  headerName: string;
  headerState: GridConfigOptions['headerState'];
  isHeaderDeletable?: boolean;
  isHeaderEditable?: boolean;
  isNewColumn?: boolean;
  isPO?: boolean;
  items: () => Signal<ExtendableDropdownOption[]>;
  onAddOption: (option: ExtendableDropdownOption) => void;
  onDelete: () => void;
  onRename: (newTitle: string) => void;
  poOptions?: Signal<{ label: string; value: string }[]>;
  removeColumnTooltip?: string;
}

export const getAttributeColumn = ({
  colId,
  field,
  headerName,
  isHeaderDeletable = true,
  isHeaderEditable = true,
  isNewColumn = false,
  isPO = false,
  items,
  onAddOption,
  onDelete,
  onRename,
  poOptions,
  removeColumnTooltip = '',
}: GetAttributeColumnParams) => {
  const width = agWidthOfAColumn({
    field: getEncodedAttributeName(headerName),
    gridData: [],
    headerName,
    maxWidth: 320,
    minWidth: 160,
    offset: 90,
  });

  return <ColDef>{
    cellClass: 'remove-padding-in-edit-mode !p-0',
    cellEditor: isPO ? AgPoDropdownComponent : AgAttributeDropdownComponent,
    cellEditorParams: isPO
      ? () => ({ poOptions })
      : () => <AgAttributeDropdownCustomParams>{ items, onAddOption },
    cellRenderer: attributeRenderer,
    colId,
    editable: true,
    field,
    headerClass: (params: HeaderClassParams) => {
      const defaultClass = ['!px-2', '!py-0', 'whitespace-normal'];
      const tooltip = params.colDef.headerTooltip || '';

      return tooltip ? [...defaultClass, 'ag-header-warning'] : defaultClass;
    },
    headerComponent: AgInlineHeaderComponent,
    headerComponentParams: <AgInlineHeaderCustomParams>{
      isHeaderEditable,
      isNewColumn,
      onDelete,
      onRename,
      removeColumnDisabled: !isHeaderDeletable,
      removeColumnTooltip,
      title: headerName,
    },
    headerName,
    maxWidth: 320,
    minWidth: 160,
    suppressHeaderKeyboardEvent: () => {
      return true;
    },
    tooltipComponentParams: <AgRawTooltipParams>{
      getNode: ({ targetNode }) => targetNode?.querySelector('div[data-value-wrapper="true"]'),
    },
    tooltipField: field,
    width,
  };
};

const attributeRenderer = (params: ICellRendererParams) => {
  const container = document.createElement('div');

  container.classList.add('flex', 'justify-between', 'h-full', 'w-full', 'items-center', 'px-2');

  const eDiv = document.createElement('div');

  eDiv.dataset.valueWrapper = 'true';
  eDiv.classList.add('truncate');
  eDiv.innerText = params.value || ZERO_HYPHEN;
  container.appendChild(eDiv);

  const icon = document.createElement('div');

  icon.innerHTML = `
    <span class="flex items-center justify-center text-[--text] cursor-pointer w-[16px] h-[16px]">
      <span class="mt-[2px] border-t-[--text] border-transparent border-t-[5px] border-x-[5px] border-b-[2.5px]"></span>
    </span>
  `;
  icon.addEventListener('click', () => {
    setTimeout(() => {
      params.api.startEditingCell({
        colKey: params.column!.getColId(),
        rowIndex: params.node.rowIndex!,
      });
    }, 0);
  });
  container.appendChild(icon);

  return container;
};
